import { formatDriverDisplayName } from '@multirx/shared/game/driverDisplayLabel.js';

/** Marqueurs HTML partagés pour les tableaux de classement Saisons. */

export function standingsRowClass(rank: number): string {
  const base = 'standings-driver-row';
  if (rank <= 3) {
    return `${base} standings-driver-row--podium standings-driver-row--rank-${rank}`;
  }
  return base;
}

export function standingsRankCellHtml(rank: number): string {
  if (rank === 1) {
    return `<span class="standings-rank-badge standings-rank-badge--gold" aria-label="1er">1</span>`;
  }
  if (rank === 2) {
    return `<span class="standings-rank-badge standings-rank-badge--silver" aria-label="2e">2</span>`;
  }
  if (rank === 3) {
    return `<span class="standings-rank-badge standings-rank-badge--bronze" aria-label="3e">3</span>`;
  }
  return `<span class="standings-rank-num">${rank}</span>`;
}

export type StandingsPointsBucket = 'championship' | 'qualification' | 'course';

export type StandingsPointsCellOptions = {
  seasonId?: number;
  driverId?: number;
  bucket?: StandingsPointsBucket;
};

function podiumClassForRank(rank: number): string {
  return rank === 1
    ? ' standings-points-pill--gold'
    : rank === 2
      ? ' standings-points-pill--silver'
      : rank === 3
        ? ' standings-points-pill--bronze'
        : '';
}

export function standingsPointsCellHtml(
  points: number,
  rank: number,
  opts?: StandingsPointsCellOptions
): string {
  const podiumClass = podiumClassForRank(rank);
  const bucket = opts?.bucket ?? 'championship';
  const tipClass =
    opts?.seasonId != null && opts?.driverId != null ? ' standings-points-pill--tip' : '';
  const ariaBucket =
    bucket === 'championship'
      ? 'championnat'
      : bucket === 'qualification'
        ? 'qualification'
        : 'course';
  const dataAttrs =
    opts?.seasonId != null && opts?.driverId != null
      ? ` data-season-id="${opts.seasonId}" data-driver-id="${opts.driverId}" data-bucket="${bucket}" tabindex="0" role="button" aria-expanded="false" aria-label="${points} points ${ariaBucket} — voir le détail (cliquer pour fixer)"`
      : '';
  return `<span class="standings-points-pill standings-points-pill--${bucket}${podiumClass}${tipClass}"${dataAttrs}>${points}</span>`;
}

/** Trois cellules `<td>` : championnat (rang) / qualification / monnaie course. */
export function standingsPointsColumnsHtml(
  championship: number,
  qualification: number,
  course: number,
  rank: number,
  opts?: { seasonId?: number; driverId?: number }
): string {
  const tipOpts =
    opts?.seasonId != null && opts?.driverId != null
      ? { seasonId: opts.seasonId, driverId: opts.driverId }
      : undefined;
  return `<td class="col-points col-points--champ">${standingsPointsCellHtml(championship, rank, {
    ...tipOpts,
    bucket: 'championship',
  })}</td>
          <td class="col-points col-points--qual">${standingsPointsCellHtml(qualification, 0, {
            ...tipOpts,
            bucket: 'qualification',
          })}</td>
          <td class="col-points col-points--course">${standingsPointsCellHtml(course, 0, {
            ...tipOpts,
            bucket: 'course',
          })}</td>`;
}

export function standingsPointsColumnsHeadHtml(): string {
  return `<th scope="col" class="col-points col-points--champ">Championnat</th>
        <th scope="col" class="col-points col-points--qual">Qualification</th>
        <th scope="col" class="col-points col-points--course">Course</th>`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export type StandingsCompactRow = {
  driverId: number;
  driverName: string;
  points: number;
  rank: number;
  division?: number;
};

/** Mini-tableau compact (cartes division / aperçus) — même badges/pastilles que le classement. */
export function standingsCompactTableHtml(
  rows: readonly StandingsCompactRow[],
  opts?: { seasonId?: number; limit?: number; ownDriverId?: number | null }
): string {
  if (rows.length === 0) return '';
  const limit = opts?.limit ?? 5;
  const tipSeason = opts?.seasonId;
  const ownDriverId = opts?.ownDriverId ?? null;
  const body = rows
    .slice(0, limit)
    .map((row) => {
      const divisionAttr = row.division != null ? ` data-division="${row.division}"` : '';
      const seasonAttr = tipSeason != null ? ` data-season-id="${tipSeason}"` : '';
      const displayName = formatDriverDisplayName(row.driverName, row.driverId, ownDriverId);
      return `<tr class="${standingsRowClass(row.rank)}" data-driver-id="${row.driverId}" data-driver-name="${escapeHtml(row.driverName)}" data-standings-rank="${row.rank}" data-standings-points="${row.points}"${divisionAttr}${seasonAttr} tabindex="0">
          <td class="col-rank">${standingsRankCellHtml(row.rank)}</td>
          <td class="col-driver">${escapeHtml(displayName)}</td>
          <td class="col-points">${standingsPointsCellHtml(row.points, row.rank, {
            seasonId: tipSeason,
            driverId: row.driverId,
            bucket: 'championship',
          })}</td>
        </tr>`;
    })
    .join('');
  return `<div class="standings-board championship-standings-mini-board">
    <table class="championship-standings-mini-table data-table data-table--standings" role="table">
      <thead><tr><th scope="col" class="col-rank">#</th><th scope="col">Pilote</th><th scope="col" class="col-points">Pts</th></tr></thead>
      <tbody>${body}</tbody>
    </table>
  </div>`;
}
